import { useSelector } from "react-redux";
import { ConfigProvider } from "antd";
import Router from "./router/Router";
import store from "./redux/store";
import * as actions from "./redux/Login/LoginTypes";
import { useEffect, useState, useRef } from "react";
import { getToken } from "firebase/messaging";
import { messaging, registerMessageListener, unregisterMessageListener } from "./firebase";
import NotificationPermissionModal from "./components/NotificationPermissionModal";
import { saveUserFcmToken } from "./redux/Settings/SettingsActions";

export default function App() {
  const customise = useSelector((state) => state.customise);
  const token = localStorage.getItem("token");
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Use ref to prevent duplicate requests
  const tokenRequestedRef = useRef(false);
  const tokenSavedRef = useRef(false);

  // Track processed message IDs to prevent duplicates
  const processedMessageIds = useRef(new Set());

  const clearCacheData = () => {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
  };

  // Consolidated function to get and save FCM token
  const requestAndSaveFCMToken = async () => {
    // Prevent duplicate requests
    if (tokenRequestedRef.current) {
      console.log("[FCM] Token request already in progress, skipping...");
      return;
    }

    // Guard against unsupported browsers (iOS Safari)
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      console.warn("[FCM] Notifications not supported, skipping token request");
      return;
    }

    tokenRequestedRef.current = true;

    try {
      const userCode = localStorage.getItem("userCode");
      if (!userCode) {
        console.warn("[FCM] No userCode found, skipping token request");
        return;
      }

      // Check if we already have a valid token
      const existingToken = localStorage.getItem("FcmToken");
      if (existingToken && tokenSavedRef.current) {
        console.log("[FCM] Valid token already exists, skipping request");
        return;
      }

      console.log("[FCM] Requesting FCM token...");

      // Wait for service worker to be ready
      const swRegistration = await navigator.serviceWorker.ready;
      console.log("[FCM] Service worker ready");

      // Get FCM token
      const vapidKey = "BJgqSdY-7gYS7wbPK4iTesSopAGl7SBXwbZzp4UfbzmKvB7CudVdJaifpdeb7ewMAIhoOM2KNoB0hy2fjel8Vv8";
      
      const currentToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration
      });

      if (currentToken) {
        console.log("[FCM] ✅ Token received:", currentToken.substring(0, 20) + "...");

        // Save only once
        if (!tokenSavedRef.current) {
          const currentDate = new Date().toISOString();
          
          localStorage.setItem("FcmToken", currentToken);
          console.log("[FCM] Token saved to localStorage");

          // Save to backend only once
          await saveUserFcmToken({
            UserId: userCode,
            FcmToken: currentToken,
            Flatfrom: "web",
            LastUpdated: currentDate,
          });
          
          tokenSavedRef.current = true;
          console.log("[FCM] ✅ Token saved to backend");

          // Optional: Show success notification
          if (Notification.permission === "granted") {
            console.log("[FCM] ✅ Notifications enabled successfully");
            // new Notification("Notifications Enabled", {
            //   body: "You will now receive notifications!",
            //   icon: "/logo192.png"
            // });
          }
        }
      } else {
        console.warn("[FCM] ❌ No token received from Firebase");
      }
    } catch (error) {
      console.error("[FCM] ❌ Error getting FCM token:", error);
      tokenRequestedRef.current = false; // Reset on error
    }
  };

  const handleAllowNotifications = async () => {
    setShowNotificationModal(false);
    
    try {
      console.log("[FCM] Requesting notification permission...");

      const permission = await Notification.requestPermission();
      console.log("[FCM] Permission result:", permission);

      if (permission === "granted") {
        // Request token after permission granted
        await requestAndSaveFCMToken();
      } else {
        console.warn("[FCM] ❌ Permission denied:", permission);
      }
    } catch (error) {
      console.error("[FCM] ❌ Error requesting permission:", error);
    }
  };

  const handleDenyNotifications = () => {
    setShowNotificationModal(false);
    console.log("[FCM] User declined notification permission");
  };

  // Check if browser supports notifications (iOS Safari does not)
  const isNotificationSupported = () => {
    return "Notification" in window && "serviceWorker" in navigator;
  };

  // Single useEffect for initialization
  useEffect(() => {
    if (token) {
      store.dispatch({
        type: actions.AUTHENTICATED,
        payload: { isAuthenticated: true },
      });
    }

    clearCacheData();

    // Skip notification/FCM setup on browsers that don't support it (e.g. iOS Safari)
    if (!isNotificationSupported()) {
      console.warn("[FCM] Notifications not supported in this browser, skipping FCM setup");
      return;
    }

    // Register service worker once
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .then((registration) => {
        console.log("[SW] Service Worker registered:", registration.scope);
        registration.update();
      })
      .catch((error) => {
        console.error("[SW] Registration failed:", error);
      });

    // Check notification permission
    if (Notification.permission === "default") {
      setShowNotificationModal(true);
    } else if (Notification.permission === "granted") {
      console.log("[FCM] Permission already granted");

      // Auto-request token if user is authenticated
      const userCode = localStorage.getItem("userCode");
      const isAuthenticated = localStorage.getItem("authenticated");

      if (isAuthenticated && userCode) {
        requestAndSaveFCMToken();
      }
    } else {
      console.warn("[FCM] Permission denied");
    }

    // Setup SINGLETON message listener - prevents duplicates globally
    registerMessageListener((payload) => {
      console.log("[App] Foreground message received:", payload);

      // Check for duplicate using messageId
      const messageId = payload.messageId || payload.fcmMessageId;
      if (messageId && processedMessageIds.current.has(messageId)) {
        console.log("[App] Duplicate message detected, skipping:", messageId);
        return;
      }

      // Mark message as processed
      if (messageId) {
        processedMessageIds.current.add(messageId);

        // Clean up old message IDs after 1 minute
        setTimeout(() => {
          processedMessageIds.current.delete(messageId);
        }, 60000);
      }

      // Show notification when app is in FOREGROUND (visible)
      // Service worker ONLY handles BACKGROUND notifications
      if (Notification.permission === "granted") {
        const title = payload.notification?.title || payload.data?.title || "New Notification";
        const body = payload.notification?.body || payload.data?.body || "You have a new message";
        const icon = payload.notification?.icon || payload.data?.icon || "/logo192.png";

        console.log("[App] Showing foreground notification:", title);

        try {
          const notification = new Notification(title, {
            body,
            icon,
            badge: "/logo192.png",
            tag: "foreground-" + Date.now(),
            requireInteraction: false,
            data: payload.data
          });

          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        } catch (error) {
          console.error("[App] Error showing notification:", error);
        }
      }
    });

    return () => {
      console.log("[App] Cleanup: unregistering message listener");
      unregisterMessageListener();
    };
  }, []); // Empty dependency array - run once on mount

  return (
    <ConfigProvider direction={customise.direction}>
      <NotificationPermissionModal
        visible={showNotificationModal}
        onAllow={handleAllowNotifications}
        onDeny={handleDenyNotifications}
      />
      <Router />
    </ConfigProvider>
  );
}